import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../utils/authUtils';
import RelationshipMemoryViewer from './brokerage/RelationshipMemoryViewer';

const BACKEND_URL = API_URL;

function getValidToken() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp * 1000 < Date.now()) return null;
    return token;
  } catch { return null; }
}

function authHeaders() {
  const token = getValidToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : null;
}

const AdminCRM = () => {
  const navigate = useNavigate();

  // Access control
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Outreach assignment creation (inline form)
  const [showAssignOutreach, setShowAssignOutreach] = useState(false);
  const [adminStaff, setAdminStaff] = useState([]);
  const [adminStaffLoaded, setAdminStaffLoaded] = useState(false);
  const [outreachForm, setOutreachForm] = useState({ assigned_to_id: '', goal: 'engagement', priority: 'normal', due_date: '', purpose: '' });
  const [outreachSubmitting, setOutreachSubmitting] = useState(false);
  const [outreachError, setOutreachError] = useState(null);
  const [outreachSuccess, setOutreachSuccess] = useState(null);

  // Contact list state
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [totalContacts, setTotalContacts] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [quickFilter, setQuickFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('');

  // Selected contact detail
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedContactType, setSelectedContactType] = useState('alumni');
  const [contactDetail, setContactDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Detail tabs
  const [detailTab, setDetailTab] = useState('overview');

  // Relationship Memory modal
  const [showRelMemory, setShowRelMemory] = useState(false);

  // Relationship Memory inline summary (for tab)
  const [relMemorySummary, setRelMemorySummary] = useState(null);
  const [relMemoryLoading, setRelMemoryLoading] = useState(false);

  // Gift form state
  const [showGiftForm, setShowGiftForm] = useState(false);
  const [giftForm, setGiftForm] = useState({
    amount: '', currency: 'USD', payment_method: '', campaign_name: '',
    fund_designation: '', gift_type: 'one_time', gift_date: '', received_date: '',
    external_reference: '', notes: ''
  });
  const [giftSaving, setGiftSaving] = useState(false);
  const [editingGiftId, setEditingGiftId] = useState(null);

  // Note form state
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteForm, setNoteForm] = useState({ note_text: '', note_type: 'general' });
  const [noteSaving, setNoteSaving] = useState(false);

  // All gifts / notes for detail tabs
  const [allGifts, setAllGifts] = useState([]);
  const [allNotes, setAllNotes] = useState([]);

  // LinkedIn activity tab state
  const [linkedinComments, setLinkedinComments] = useState([]);
  const [linkedinCommentsLoading, setLinkedinCommentsLoading] = useState(false);
  const [linkedinCommentsTotal, setLinkedinCommentsTotal] = useState(0);

  // Timeline state
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineTotal, setTimelineTotal] = useState(0);
  const [timelinePage, setTimelinePage] = useState(1);

  // Versium/DataFinder scores state
  const [versiumScores, setVersiumScores] = useState(null);
  const [versiumLoading, setVersiumLoading] = useState(false);
  const [versiumError, setVersiumError] = useState(null);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Check access
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const auth = authHeaders();
        if (!auth) { setAccessDenied(true); setAccessChecked(true); return; }
        const res = await axios.get(`${BACKEND_URL}/api/admin/me`, auth);
        const data = res.data;
        if (data?.is_super_admin) setIsSuperAdmin(true);
        // Superadmins always get access; non-superadmins blocked if hide_crm is true
        if (data?.hide_crm === true && !data?.is_super_admin) {
          setAccessDenied(true);
        }
      } catch (err) {
        console.error('CRM access check failed:', err?.response?.status, err?.response?.data || err.message);
        // If the /api/admin/me endpoint fails, try to decode the token directly
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (payload.role === 'admin' && payload.is_super_admin) {
              // Superadmin token — allow access even if /me endpoint failed
              setIsSuperAdmin(true);
              setAccessChecked(true);
              return;
            }
          }
        } catch {}
        setAccessDenied(true);
      } finally {
        setAccessChecked(true);
      }
    };
    checkAccess();
  }, []);

  // Error display
  const [contactsError, setContactsError] = useState(null);

  // Fetch contacts
  const fetchContacts = useCallback(async (pageNum = 1) => {
    const auth = authHeaders();
    if (!auth) return;
    setContactsLoading(true);
    setContactsError(null);
    try {
      const params = { page: pageNum, per_page: 50, sort_by: 'name', sort_dir: 'asc' };
      if (search) params.search = search;
      if (tierFilter) params.tier_filter = tierFilter;
      if (quickFilter === 'has_gifts') params.has_gifts = true;
      if (quickFilter === 'no_gifts') params.has_gifts = false;
      if (quickFilter === 'has_scores') params.has_scores = true;
      if (quickFilter === 'no_scores') params.has_scores = false;
      if (sourceFilter) params.source_filter = sourceFilter;

      const res = await axios.get(`${BACKEND_URL}/api/admin/crm/contacts`, { ...auth, params });
      setContacts(res.data.contacts || []);
      setTotalContacts(res.data.total || 0);
      setPage(res.data.page || 1);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      console.error('Error fetching CRM contacts:', err);
      const detail = err?.response?.data?.detail || err.message || 'Unknown error';
      setContactsError(`Failed to load contacts: ${detail} (HTTP ${err?.response?.status || '?'})`);
    } finally {
      setContactsLoading(false);
    }
  }, [search, tierFilter, quickFilter, sourceFilter]);

  useEffect(() => {
    if (accessChecked && !accessDenied) {
      fetchContacts(1);
    }
  }, [accessChecked, accessDenied, fetchContacts]);

  // Fetch contact detail
  const fetchContactDetail = useCallback(async (id, contactType = 'alumni') => {
    const auth = authHeaders();
    if (!auth) return;
    setDetailLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/crm/contacts/${id}`, { ...auth, params: { contact_type: contactType } });
      setContactDetail(res.data);
      setVersiumScores(res.data.versium_scores || null);
      setVersiumError(null);
    } catch (err) {
      console.error('Error fetching contact detail:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Fetch all gifts for a contact
  const fetchAllGifts = useCallback(async (id, contactType) => {
    const auth = authHeaders();
    if (!auth) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/crm/contacts/${id}/gifts`, { ...auth, params: { per_page: 200, contact_type: contactType || 'alumni' } });
      setAllGifts(res.data.gifts || []);
    } catch (err) {
      console.error('Error fetching gifts:', err);
    }
  }, []);

  // Fetch all notes for a contact
  const fetchAllNotes = useCallback(async (id, contactType) => {
    const auth = authHeaders();
    if (!auth) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/crm/contacts/${id}/notes`, { ...auth, params: { per_page: 200, contact_type: contactType || 'alumni' } });
      setAllNotes(res.data.notes || []);
    } catch (err) {
      console.error('Error fetching notes:', err);
    }
  }, []);

  // Fetch LinkedIn comments for a contact
  const fetchLinkedInActivity = useCallback(async (id, contactType) => {
    const auth = authHeaders();
    if (!auth) return;
    setLinkedinCommentsLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/crm/contacts/${id}/linkedin-activity`, {
        ...auth,
        params: { contact_type: contactType || 'alumni', page_size: 50 }
      });
      setLinkedinComments(res.data.items || []);
      setLinkedinCommentsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching LinkedIn activity:', err);
      setLinkedinComments([]);
      setLinkedinCommentsTotal(0);
    } finally {
      setLinkedinCommentsLoading(false);
    }
  }, []);

  // Fetch timeline
  const fetchTimeline = useCallback(async (id, pg = 1, contactType) => {
    const auth = authHeaders();
    if (!auth) return;
    setTimelineLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/crm/contacts/${id}/timeline`, { ...auth, params: { page: pg, per_page: 50, contact_type: contactType || 'alumni' } });
      setTimelineEvents(res.data.events || []);
      setTimelineTotal(res.data.total || 0);
      setTimelinePage(res.data.page || 1);
    } catch (err) {
      console.error('Error fetching timeline:', err);
    } finally {
      setTimelineLoading(false);
    }
  }, []);

  // Fetch relationship memory summary for a given exec_person_id
  const fetchRelMemorySummary = useCallback(async (execPersonId) => {
    if (!execPersonId) { setRelMemorySummary(null); return; }
    const auth = authHeaders();
    if (!auth) return;
    setRelMemoryLoading(true);
    try {
      const [memRes, cooldownRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/staff/brokerage/executives/${execPersonId}/memory`, auth),
        axios.get(`${BACKEND_URL}/api/staff/brokerage/executives/${execPersonId}/cooldown-check`, auth),
      ]);
      setRelMemorySummary({ memory: memRes.data, cooldown: cooldownRes.data });
    } catch (err) {
      // 404 means no memory record — that's fine
      if (err?.response?.status === 404) {
        setRelMemorySummary(null);
      } else {
        console.error('Error fetching relationship memory:', err);
        setRelMemorySummary(null);
      }
    } finally {
      setRelMemoryLoading(false);
    }
  }, []);

  // Fetch Versium/DataFinder predictive scores
  const fetchVersiumScore = async (scoreType) => {
    if (!contactDetail?.exec_contact_id) return;
    setVersiumLoading(true);
    setVersiumError(null);
    try {
      const auth = authHeaders();
      const res = await axios.post(
        `${BACKEND_URL}/api/exec/contacts/${contactDetail.exec_contact_id}/versium-scores`,
        { output_types: scoreType === 'both' ? ['donor_score', 'wealth_score']
            : scoreType === 'donor' ? ['donor_score'] : ['wealth_score'],
          force_refresh: true },
        auth
      );
      setVersiumScores(res.data);
    } catch (err) {
      setVersiumError(err.response?.data?.detail || 'Failed to fetch scores');
    } finally {
      setVersiumLoading(false);
    }
  };

  const selectContact = (id, contactType = 'alumni') => {
    setSelectedContactId(id);
    setSelectedContactType(contactType);
    setDetailTab('overview');
    fetchContactDetail(id, contactType);
    fetchAllGifts(id, contactType);
    fetchAllNotes(id, contactType);
  };

  // Load LinkedIn activity when tab switches
  useEffect(() => {
    if (detailTab === 'linkedin-activity' && selectedContactId) {
      fetchLinkedInActivity(selectedContactId, selectedContactType);
    }
  }, [detailTab, selectedContactId, selectedContactType, fetchLinkedInActivity]);

  // Load timeline when tab switches to timeline
  useEffect(() => {
    if (detailTab === 'timeline' && selectedContactId) {
      fetchTimeline(selectedContactId, 1, selectedContactType);
    }
  }, [detailTab, selectedContactId, selectedContactType, fetchTimeline]);

  // Save gift
  const saveGift = async () => {
    const auth = authHeaders();
    if (!auth || !selectedContactId) return;
    setGiftSaving(true);
    try {
      const payload = {
        amount_cents: Math.round(parseFloat(giftForm.amount) * 100),
        currency: giftForm.currency,
        payment_method: giftForm.payment_method || null,
        campaign_name: giftForm.campaign_name || null,
        fund_designation: giftForm.fund_designation || null,
        gift_type: giftForm.gift_type,
        gift_date: giftForm.gift_date,
        received_date: giftForm.received_date || null,
        external_reference: giftForm.external_reference || null,
        notes: giftForm.notes || null,
      };

      if (editingGiftId) {
        await axios.put(`${BACKEND_URL}/api/admin/crm/gifts/${editingGiftId}`, payload, auth);
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/crm/contacts/${selectedContactId}/gifts?contact_type=${selectedContactType}`, payload, auth);
      }

      setShowGiftForm(false);
      setEditingGiftId(null);
      setGiftForm({ amount: '', currency: 'USD', payment_method: '', campaign_name: '', fund_designation: '', gift_type: 'one_time', gift_date: '', received_date: '', external_reference: '', notes: '' });
      fetchContactDetail(selectedContactId, selectedContactType);
      fetchAllGifts(selectedContactId, selectedContactType);
      fetchContacts(page);
    } catch (err) {
      alert('Error saving gift: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGiftSaving(false);
    }
  };

  // Delete gift
  const deleteGift = async (giftId) => {
    if (!window.confirm('Delete this gift record?')) return;
    const auth = authHeaders();
    if (!auth) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/crm/gifts/${giftId}`, auth);
      fetchContactDetail(selectedContactId, selectedContactType);
      fetchAllGifts(selectedContactId, selectedContactType);
      fetchContacts(page);
    } catch (err) {
      alert('Error deleting gift: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Save note
  const saveNote = async () => {
    const auth = authHeaders();
    if (!auth || !selectedContactId) return;
    setNoteSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/crm/contacts/${selectedContactId}/notes?contact_type=${selectedContactType}`, {
        note_text: noteForm.note_text,
        note_type: noteForm.note_type,
      }, auth);
      setShowNoteForm(false);
      setNoteForm({ note_text: '', note_type: 'general' });
      fetchContactDetail(selectedContactId, selectedContactType);
      fetchAllNotes(selectedContactId, selectedContactType);
    } catch (err) {
      alert('Error saving note: ' + (err.response?.data?.detail || err.message));
    } finally {
      setNoteSaving(false);
    }
  };

  // Delete note
  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    const auth = authHeaders();
    if (!auth) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/crm/notes/${noteId}`, auth);
      fetchContactDetail(selectedContactId, selectedContactType);
      fetchAllNotes(selectedContactId, selectedContactType);
    } catch (err) {
      alert('Error deleting note: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Export contacts CSV
  const exportContactsCSV = async () => {
    const auth = authHeaders();
    if (!auth) return;
    setExporting(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (tierFilter) params.tier_filter = tierFilter;
      if (quickFilter === 'has_gifts') params.has_gifts = true;
      if (quickFilter === 'no_gifts') params.has_gifts = false;
      if (quickFilter === 'has_scores') params.has_scores = true;
      if (quickFilter === 'no_scores') params.has_scores = false;
      if (sourceFilter) params.source_filter = sourceFilter;

      const res = await axios.get(`${BACKEND_URL}/api/admin/crm/contacts/export`, {
        ...auth, params, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `crm_contacts_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error exporting contacts: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  // Export gifts CSV for selected contact
  const exportGiftsCSV = async () => {
    if (!selectedContactId) return;
    const auth = authHeaders();
    if (!auth) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/crm/contacts/${selectedContactId}/gifts/export?contact_type=${selectedContactType}`, {
        ...auth, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const name = contactDetail ? `${contactDetail.last_name}_${contactDetail.first_name}` : selectedContactId;
      link.setAttribute('download', `crm_gifts_${name}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error exporting gifts: ' + (err.response?.data?.detail || err.message));
    }
  };

  const formatCurrency = (cents) => {
    if (!cents && cents !== 0) return '$0.00';
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Timeline event type styling
  const eventTypeConfig = {
    gift: { icon: '$', bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' },
    note: { icon: 'N', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
    touchpoint: { icon: 'T', bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-300' },
    membership: { icon: 'M', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
    outreach_assigned: { icon: 'A', bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' },
    outreach_closed: { icon: 'C', bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
  };

  if (!accessChecked) return <div className="flex items-center justify-center h-screen"><div className="text-gray-500">Loading...</div></div>;
  if (accessDenied) return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <h2 className="text-xl font-bold text-red-600 mb-2">Access Denied</h2>
        <p className="text-gray-500">You do not have permission to access the CRM.</p>
        <button onClick={() => navigate('/admin')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg">Back to Admin</button>
      </div>
    </div>
  );

  const noteTypeColors = {
    general: 'bg-gray-100 text-gray-700',
    call: 'bg-green-100 text-green-700',
    email: 'bg-blue-100 text-blue-700',
    meeting: 'bg-purple-100 text-purple-700',
    linkedin: 'bg-sky-100 text-sky-700',
    event: 'bg-amber-100 text-amber-700',
    internal: 'bg-red-100 text-red-700',
  };

  // Lazy-load admin staff list for outreach assignment form
  const loadAdminStaff = async () => {
    if (adminStaffLoaded) return;
    try {
      const auth = authHeaders();
      if (!auth) return;
      const res = await axios.get(`${BACKEND_URL}/api/admin/admin-users`, auth);
      const active = (res.data || []).filter(a => a.is_active !== false);
      setAdminStaff(active);
      setAdminStaffLoaded(true);
    } catch (err) {
      console.error('Failed to load admin staff:', err);
    }
  };

  const handleAssignOutreach = async () => {
    if (!outreachForm.assigned_to_id || !selectedContactId) return;
    setOutreachSubmitting(true);
    setOutreachError(null);
    setOutreachSuccess(null);
    try {
      const auth = authHeaders();
      if (!auth) return;
      // Build assignment payload: exec contacts use exec_contact_id (UUID), alumni use alumni_user_id (integer)
      const assignmentPayload = {
        assigned_to_id: parseInt(outreachForm.assigned_to_id),
        goal: outreachForm.goal || 'engagement',
        priority: outreachForm.priority || 'normal',
        due_date: outreachForm.due_date || null,
        purpose: outreachForm.purpose || null
      };
      if (selectedContactType === 'exec_contact') {
        assignmentPayload.exec_contact_id = selectedContactId;
      } else {
        assignmentPayload.alumni_user_id = selectedContactId;
      }
      await axios.post(`${BACKEND_URL}/api/exec/outreach/assign`, {
        assignments: [assignmentPayload]
      }, auth);
      setOutreachSuccess('Outreach assignment created successfully.');
      setShowAssignOutreach(false);
      setOutreachForm({ assigned_to_id: '', goal: 'engagement', priority: 'normal', due_date: '', purpose: '' });
      // Re-fetch contact detail to show the new active outreach
      if (selectedContactId) {
        const detRes = await axios.get(`${BACKEND_URL}/api/admin/crm/contacts/${selectedContactId}?contact_type=${selectedContactType}`, auth);
        setContactDetail(detRes.data);
        setVersiumScores(detRes.data.versium_scores || null);
      }
    } catch (err) {
      setOutreachError(err?.response?.data?.detail || 'Failed to create outreach assignment.');
    } finally {
      setOutreachSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="text-gray-500 hover:text-gray-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900">CRM - Contacts</h1>
            <span className="text-sm text-gray-500">({totalContacts} contacts)</span>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (<>
              <button
                onClick={() => navigate('/admin/outreach-manager')}
                className="px-3 py-1.5 bg-violet-50 text-violet-700 border border-violet-200 rounded-lg hover:bg-violet-100 text-xs font-medium"
              >
                Outreach Manager
              </button>
              <button
                onClick={() => navigate('/admin/executive-intelligence?tab=imports&section=unprovisioned')}
                className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-xs font-medium"
              >
                Research
              </button>
              <button
                onClick={() => navigate('/admin/executive-intelligence?tab=brokerage')}
                className="px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 text-xs font-medium"
              >
                Executive Intel
              </button>
              <button
                onClick={() => navigate('/admin/membership-manager')}
                className="px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 text-xs font-medium"
              >
                Membership
              </button>
              <button
                onClick={() => navigate('/admin/swee')}
                className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs font-medium"
              >
                SWEE
              </button>
            </>)}
            <button
              onClick={exportContactsCSV}
              disabled={exporting}
              className="px-3 py-2 text-sm bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b px-6 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }}
              placeholder="Search name, email, company, university..."
              className="flex-1 max-w-md px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
            />
            <button
              onClick={() => { setSearch(searchInput); setPage(1); }}
              className="px-3 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700"
            >
              Search
            </button>
            {search && (
              <button onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }} className="text-xs text-gray-500 hover:text-gray-700">Clear</button>
            )}
          </div>

          <select
            value={tierFilter}
            onChange={(e) => { setTierFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
          >
            <option value="">All Tiers</option>
            <option value="community">Community</option>
            <option value="professional">Professional</option>
            <option value="leader">Leader</option>
            <option value="fellow">Fellow</option>
          </select>
        </div>

        <div className="flex items-center gap-4 mt-2">
          {/* Source filter */}
          <div className="flex gap-1.5">
            {[
              { key: '', label: 'All Sources' },
              { key: 'alumni', label: 'Alumni' },
              { key: 'exec_contact', label: 'Exec Contacts' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => { setSourceFilter(f.key); setPage(1); }}
                className={`px-3 py-1 text-xs rounded-full ${sourceFilter === f.key ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="w-px h-4 bg-gray-300"></div>

          {/* Quick filters */}
          <div className="flex gap-1.5">
            {[
              { key: 'all', label: 'All' },
              { key: 'has_gifts', label: 'Has Gifts' },
              { key: 'no_gifts', label: 'No Gifts' },
              { key: 'has_scores', label: 'Has Scores' },
              { key: 'no_scores', label: 'No Scores' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => { setQuickFilter(f.key); setPage(1); }}
                className={`px-3 py-1 text-xs rounded-full ${quickFilter === f.key ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main content: list + detail */}
      <div className="flex" style={{ height: 'calc(100vh - 165px)' }}>
        {/* Contact List (left panel) */}
        <div className={`${selectedContactId ? 'w-2/5' : 'w-full'} border-r overflow-y-auto bg-white transition-all`}>
          {contactsError ? (
            <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
              <div className="text-red-600 font-medium mb-2">Error Loading Contacts</div>
              <div className="text-red-500 text-sm mb-4">{contactsError}</div>
              <button onClick={() => fetchContacts(1)} className="px-4 py-2 bg-violet-600 text-white rounded hover:bg-violet-700 text-sm">Retry</button>
            </div>
          ) : contactsLoading ? (
            <div className="flex items-center justify-center py-20 text-gray-500">Loading contacts...</div>
          ) : contacts.length === 0 ? (
            <div className="flex items-center justify-center py-20 text-gray-500">
              {search || tierFilter || quickFilter !== 'all' || sourceFilter ? 'No contacts match your filters' : 'No contacts in the database'}
            </div>
          ) : (
            <>
              {contacts.map(c => {
                const isSelected = selectedContactId === c.id && selectedContactType === c.contact_type;
                return (
                  <div
                    key={`${c.contact_type}_${c.id}`}
                    onClick={() => selectContact(c.id, c.contact_type)}
                    className={`px-4 py-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-violet-50 border-l-4 border-l-violet-500' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-sm text-gray-900">{c.last_name || '?'}, {c.first_name || '?'}</span>
                          <span className={`px-1.5 py-0.5 text-[9px] rounded font-medium ${
                            c.contact_type === 'alumni' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {c.contact_type === 'alumni' ? 'Alumni' : 'Contact'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">{c.current_position ? `${c.current_position} @ ` : ''}{c.current_employer || 'No company'}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {c.membership && (
                          <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${
                            c.membership.tier_slug === 'fellow' ? 'bg-amber-100 text-amber-700' :
                            c.membership.tier_slug === 'leader' ? 'bg-purple-100 text-purple-700' :
                            c.membership.tier_slug === 'professional' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {c.membership.tier_name}
                          </span>
                        )}
                        {c.gifts?.count > 0 && (
                          <span className="px-2 py-0.5 text-[10px] rounded-full bg-emerald-100 text-emerald-700 font-medium">
                            {formatCurrency(c.gifts.total_cents)}
                          </span>
                        )}
                        {c.outreach && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-violet-100 text-violet-700 font-medium">
                            {c.outreach.stage}
                          </span>
                        )}
                        {c.source_system && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-gray-100 text-gray-500">
                            {c.source_system}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                  <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
                  <div className="flex gap-1">
                    <button onClick={() => fetchContacts(page - 1)} disabled={page <= 1} className="px-3 py-1 text-xs border rounded disabled:opacity-50">Prev</button>
                    <button onClick={() => fetchContacts(page + 1)} disabled={page >= totalPages} className="px-3 py-1 text-xs border rounded disabled:opacity-50">Next</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Contact Detail (right panel) */}
        {selectedContactId && (
          <div className="flex-1 overflow-y-auto bg-gray-50">
            {detailLoading ? (
              <div className="flex items-center justify-center py-20 text-gray-500">Loading contact...</div>
            ) : contactDetail ? (
              <div>
                {/* Contact header */}
                <div className="bg-white px-6 py-4 border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-gray-900">
                          {contactDetail.full_name || `${contactDetail.first_name} ${contactDetail.last_name}`}
                        </h2>
                        <span className={`px-2 py-0.5 text-[10px] rounded font-medium ${
                          contactDetail.contact_type === 'alumni' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {contactDetail.contact_type === 'alumni' ? 'Alumni' : 'Exec Contact'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">{contactDetail.current_position ? `${contactDetail.current_position} @ ` : ''}{contactDetail.current_employer || ''}</div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        {contactDetail.email && <span>{contactDetail.email}</span>}
                        {contactDetail.phone && <span>| {contactDetail.phone}</span>}
                        {contactDetail.linkedin_url && (
                          <a href={contactDetail.linkedin_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">LinkedIn</a>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {contactDetail.membership && (
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                          contactDetail.membership.tier_slug === 'fellow' ? 'bg-amber-100 text-amber-700' :
                          contactDetail.membership.tier_slug === 'leader' ? 'bg-purple-100 text-purple-700' :
                          contactDetail.membership.tier_slug === 'professional' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {contactDetail.membership.tier_name}
                        </span>
                      )}
                      <button
                        onClick={() => { setSelectedContactId(null); setContactDetail(null); setSelectedContactType('alumni'); }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Detail tabs */}
                <div className="bg-white border-b px-6">
                  <div className="flex gap-4">
                    {(contactDetail.contact_type === 'alumni'
                      ? ['overview', 'gifts', 'notes', 'scores', 'outreach', 'linkedin-activity', ...(contactDetail.exec_person_id ? ['relationship'] : []), 'timeline']
                      : ['overview', 'gifts', 'notes', 'scores', 'outreach', 'linkedin-activity', ...(contactDetail.exec_person_id ? ['relationship'] : []), 'timeline']
                    ).map(tab => (
                      <button
                        key={tab}
                        onClick={() => {
                          setDetailTab(tab);
                          if (tab === 'relationship' && contactDetail.exec_person_id) {
                            fetchRelMemorySummary(contactDetail.exec_person_id);
                          }
                        }}
                        className={`py-2 px-1 text-sm font-medium border-b-2 ${detailTab === tab ? 'border-violet-500 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                      >
                        {tab === 'relationship' ? 'Relationship Memory' : tab === 'linkedin-activity' ? 'LinkedIn Activity' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                        {tab === 'gifts' && contactDetail.gifts_summary?.gift_count > 0 && ` (${contactDetail.gifts_summary.gift_count})`}
                        {tab === 'notes' && contactDetail.notes_count > 0 && ` (${contactDetail.notes_count})`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab content */}
                <div className="p-6">
                  {/* Overview Tab */}
                  {detailTab === 'overview' && (
                    <div className="space-y-4">
                      {/* Contact Info Card */}
                      <div className="bg-white rounded-lg border p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h3>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-gray-500">Email:</span> <span className="text-gray-900">{contactDetail.email || 'N/A'}</span></div>
                          <div><span className="text-gray-500">Phone:</span> <span className="text-gray-900">{contactDetail.phone || 'N/A'}</span></div>
                          {contactDetail.additional_emails?.length > 0 && (
                            <div className="col-span-2"><span className="text-gray-500">Other Emails:</span> <span className="text-gray-900">{contactDetail.additional_emails.join(', ')}</span></div>
                          )}
                          {contactDetail.mailing_address && (
                            <div className="col-span-2"><span className="text-gray-500">Address:</span> <span className="text-gray-900">{[contactDetail.mailing_address.street, contactDetail.mailing_address.city, contactDetail.mailing_address.state, contactDetail.mailing_address.zip].filter(Boolean).join(', ')}</span></div>
                          )}
                          {contactDetail.source_system && (
                            <div><span className="text-gray-500">Source:</span> <span className="text-gray-900">{contactDetail.source_system}</span></div>
                          )}
                          {contactDetail.is_linkedin_follower && (
                            <div><span className="text-gray-500">LinkedIn Follower:</span> <span className="text-green-600 font-medium">Yes</span></div>
                          )}
                          {contactDetail.linkedin_connected_on && (
                            <div><span className="text-gray-500">Connected:</span> <span className="text-gray-900">{contactDetail.linkedin_connected_on}</span></div>
                          )}
                        </div>
                      </div>

                      {/* Career & Education Card */}
                      <div className="bg-white rounded-lg border p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">Career & Education</h3>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-gray-500">Company:</span> <span className="text-gray-900">{contactDetail.current_employer || 'N/A'}</span></div>
                          <div><span className="text-gray-500">Position:</span> <span className="text-gray-900">{contactDetail.current_position || 'N/A'}</span></div>
                          <div><span className="text-gray-500">University:</span> <span className="text-gray-900">{contactDetail.university || 'N/A'}</span></div>
                          {contactDetail.contact_type === 'alumni' && (
                            <>
                              <div><span className="text-gray-500">Major:</span> <span className="text-gray-900">{contactDetail.major || 'N/A'}</span></div>
                              <div><span className="text-gray-500">Degree:</span> <span className="text-gray-900">{contactDetail.degree_type || 'N/A'}</span></div>
                            </>
                          )}
                          <div><span className="text-gray-500">Grad Year:</span> <span className="text-gray-900">{contactDetail.graduation_year || 'N/A'}</span></div>
                        </div>
                      </div>

                      {/* Research/Match Status — exec contacts only */}
                      {contactDetail.contact_type === 'exec_contact' && (contactDetail.research_status || contactDetail.match_status) && (
                        <div className="bg-white rounded-lg border p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-3">Research & Match Status</h3>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            {contactDetail.research_status && (
                              <div><span className="text-gray-500">Research:</span> <span className="capitalize text-gray-900">{contactDetail.research_status}</span></div>
                            )}
                            {contactDetail.match_status && (
                              <div><span className="text-gray-500">Match:</span> <span className="capitalize text-gray-900">{contactDetail.match_status}</span></div>
                            )}
                            {contactDetail.research_note && (
                              <div className="col-span-2"><span className="text-gray-500">Note:</span> <span className="text-gray-900">{contactDetail.research_note}</span></div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Membership & Giving Summary — alumni only */}
                      {contactDetail.contact_type === 'alumni' && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white rounded-lg border p-4">
                            <h3 className="text-sm font-semibold text-gray-700 mb-2">Membership</h3>
                            {contactDetail.membership ? (
                              <div className="text-sm">
                                <div className="font-medium text-gray-900">{contactDetail.membership.tier_name}</div>
                                <div className="text-gray-500">Status: {contactDetail.membership.status}</div>
                                {contactDetail.membership.expires_at && <div className="text-gray-500">Expires: {new Date(contactDetail.membership.expires_at).toLocaleDateString()}</div>}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500">No active membership</div>
                            )}
                          </div>
                          <div className="bg-white rounded-lg border p-4">
                            <h3 className="text-sm font-semibold text-gray-700 mb-2">Giving</h3>
                            <div className="text-sm">
                              <div className="font-medium text-gray-900">{formatCurrency(contactDetail.gifts_summary?.total_given_cents)}</div>
                              <div className="text-gray-500">{contactDetail.gifts_summary?.gift_count || 0} gifts</div>
                              {contactDetail.gifts_summary?.last_gift_date && <div className="text-gray-500">Last: {contactDetail.gifts_summary.last_gift_date}</div>}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Active Outreach — alumni only */}
                      {contactDetail.contact_type === 'alumni' && contactDetail.active_outreach && (
                        <div className="bg-violet-50 rounded-lg border border-violet-200 p-4">
                          <h3 className="text-sm font-semibold text-violet-700 mb-2">Active Outreach</h3>
                          <div className="text-sm">
                            <div className="text-gray-700">Assigned to: <span className="font-medium">{contactDetail.active_outreach.assigned_to_name}</span></div>
                            <div className="text-gray-700">Stage: <span className="font-medium capitalize">{contactDetail.active_outreach.stage}</span></div>
                            {contactDetail.active_outreach.goal && <div className="text-gray-700">Goal: {contactDetail.active_outreach.goal}</div>}
                            {contactDetail.active_outreach.due_date && <div className="text-gray-700">Due: {contactDetail.active_outreach.due_date}</div>}
                          </div>
                        </div>
                      )}

                      {/* Exec Profile Data */}
                      {contactDetail.exec_profile && (
                        <div className="bg-sky-50 rounded-lg border border-sky-200 p-4">
                          <h3 className="text-sm font-semibold text-sky-700 mb-2">Executive Profile</h3>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            {contactDetail.exec_profile.headline && <div className="col-span-2"><span className="text-gray-500">Headline:</span> <span className="text-gray-900">{contactDetail.exec_profile.headline}</span></div>}
                            {contactDetail.exec_profile.current_company && <div><span className="text-gray-500">Company:</span> <span className="text-gray-900">{contactDetail.exec_profile.current_company}</span></div>}
                            {contactDetail.exec_profile.current_title && <div><span className="text-gray-500">Title:</span> <span className="text-gray-900">{contactDetail.exec_profile.current_title}</span></div>}
                            {contactDetail.exec_profile.location_raw && <div><span className="text-gray-500">Location:</span> <span className="text-gray-900">{contactDetail.exec_profile.location_raw}</span></div>}
                          </div>
                        </div>
                      )}

                      {/* Exec Person link — exec contacts */}
                      {contactDetail.contact_type === 'exec_contact' && contactDetail.exec_person_id && (
                        <div className="bg-gray-50 rounded-lg border p-3">
                          <div className="text-xs text-gray-500">Exec Person ID: <span className="font-mono text-gray-700">{contactDetail.exec_person_id}</span></div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Gifts Tab */}
                  {detailTab === 'gifts' && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-700">Gifts ({allGifts.length})</h3>
                        <div className="flex gap-2">
                          {allGifts.length > 0 && (
                            <button
                              onClick={exportGiftsCSV}
                              className="px-3 py-1.5 text-xs bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                            >
                              Export CSV
                            </button>
                          )}
                          <button
                            onClick={() => { setShowGiftForm(true); setEditingGiftId(null); setGiftForm({ amount: '', currency: 'USD', payment_method: '', campaign_name: '', fund_designation: '', gift_type: 'one_time', gift_date: new Date().toISOString().split('T')[0], received_date: '', external_reference: '', notes: '' }); }}
                            className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                          >
                            + Add Gift
                          </button>
                        </div>
                      </div>

                      {/* Gift Form Modal */}
                      {showGiftForm && (
                        <div className="bg-white rounded-lg border p-4 mb-4">
                          <h4 className="text-sm font-semibold mb-3">{editingGiftId ? 'Edit Gift' : 'New Gift'}</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Amount ($) *</label>
                              <input type="number" step="0.01" value={giftForm.amount} onChange={(e) => setGiftForm({...giftForm, amount: e.target.value})} className="w-full px-2 py-1.5 text-sm border rounded" placeholder="100.00" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Date *</label>
                              <input type="date" value={giftForm.gift_date} onChange={(e) => setGiftForm({...giftForm, gift_date: e.target.value})} className="w-full px-2 py-1.5 text-sm border rounded" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Payment Method</label>
                              <select value={giftForm.payment_method} onChange={(e) => setGiftForm({...giftForm, payment_method: e.target.value})} className="w-full px-2 py-1.5 text-sm border rounded">
                                <option value="">Select...</option>
                                <option value="credit_card">Credit Card</option>
                                <option value="check">Check</option>
                                <option value="wire">Wire Transfer</option>
                                <option value="paypal">PayPal</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Gift Type</label>
                              <select value={giftForm.gift_type} onChange={(e) => setGiftForm({...giftForm, gift_type: e.target.value})} className="w-full px-2 py-1.5 text-sm border rounded">
                                <option value="one_time">One-time</option>
                                <option value="recurring">Recurring</option>
                                <option value="pledge">Pledge</option>
                                <option value="in_kind">In-kind</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Campaign</label>
                              <input type="text" value={giftForm.campaign_name} onChange={(e) => setGiftForm({...giftForm, campaign_name: e.target.value})} className="w-full px-2 py-1.5 text-sm border rounded" placeholder="e.g. 2026 Annual Fund" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Fund Designation</label>
                              <input type="text" value={giftForm.fund_designation} onChange={(e) => setGiftForm({...giftForm, fund_designation: e.target.value})} className="w-full px-2 py-1.5 text-sm border rounded" placeholder="e.g. Scholarship Fund" />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-600 mb-1">Notes</label>
                              <textarea value={giftForm.notes} onChange={(e) => setGiftForm({...giftForm, notes: e.target.value})} className="w-full px-2 py-1.5 text-sm border rounded" rows={2} />
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <button onClick={saveGift} disabled={giftSaving || !giftForm.amount || !giftForm.gift_date} className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                              {giftSaving ? 'Saving...' : 'Save Gift'}
                            </button>
                            <button onClick={() => setShowGiftForm(false)} className="px-3 py-1.5 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
                          </div>
                        </div>
                      )}

                      {/* Gift list */}
                      {allGifts.length === 0 ? (
                        <div className="text-center text-sm text-gray-500 py-8">No gifts recorded</div>
                      ) : (
                        <div className="space-y-2">
                          {allGifts.map(g => (
                            <div key={g.id} className="bg-white rounded-lg border p-3 flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm text-gray-900">{formatCurrency(g.amount_cents)}</span>
                                  <span className="text-xs text-gray-500">{g.gift_date}</span>
                                  {g.gift_type && g.gift_type !== 'one_time' && <span className="px-1.5 py-0.5 text-[10px] rounded bg-blue-100 text-blue-700">{g.gift_type}</span>}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {[g.campaign_name, g.fund_designation, g.payment_method].filter(Boolean).join(' | ') || 'No details'}
                                </div>
                                {g.notes && <div className="text-xs text-gray-600 mt-1 italic">{g.notes}</div>}
                              </div>
                              <button onClick={() => deleteGift(g.id)} className="text-red-400 hover:text-red-600 text-xs">Delete</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notes Tab */}
                  {detailTab === 'notes' && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-700">Notes ({allNotes.length})</h3>
                        <button
                          onClick={() => { setShowNoteForm(true); setNoteForm({ note_text: '', note_type: 'general' }); }}
                          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          + Add Note
                        </button>
                      </div>

                      {/* Note Form */}
                      {showNoteForm && (
                        <div className="bg-white rounded-lg border p-4 mb-4">
                          <h4 className="text-sm font-semibold mb-3">New Note</h4>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Type</label>
                              <select value={noteForm.note_type} onChange={(e) => setNoteForm({...noteForm, note_type: e.target.value})} className="px-2 py-1.5 text-sm border rounded">
                                <option value="general">General</option>
                                <option value="call">Call</option>
                                <option value="email">Email</option>
                                <option value="meeting">Meeting</option>
                                <option value="linkedin">LinkedIn</option>
                                <option value="event">Event</option>
                                <option value="internal">Internal</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Note *</label>
                              <textarea value={noteForm.note_text} onChange={(e) => setNoteForm({...noteForm, note_text: e.target.value})} className="w-full px-2 py-1.5 text-sm border rounded" rows={4} placeholder="Enter note..." />
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <button onClick={saveNote} disabled={noteSaving || !noteForm.note_text.trim()} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                              {noteSaving ? 'Saving...' : 'Save Note'}
                            </button>
                            <button onClick={() => setShowNoteForm(false)} className="px-3 py-1.5 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Cancel</button>
                          </div>
                        </div>
                      )}

                      {/* Note list */}
                      {allNotes.length === 0 ? (
                        <div className="text-center text-sm text-gray-500 py-8">No notes recorded</div>
                      ) : (
                        <div className="space-y-2">
                          {allNotes.map(n => (
                            <div key={n.id} className="bg-white rounded-lg border p-3">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${noteTypeColors[n.note_type] || noteTypeColors.general}`}>
                                    {n.note_type}
                                  </span>
                                  <span className="text-xs text-gray-500">{n.created_by_name}</span>
                                  <span className="text-xs text-gray-400">{n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</span>
                                </div>
                                <button onClick={() => deleteNote(n.id)} className="text-red-400 hover:text-red-600 text-xs">Delete</button>
                              </div>
                              <div className="text-sm text-gray-800 whitespace-pre-wrap">{n.note_text}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Scores Tab */}
                  {detailTab === 'scores' && (
                    <div className="space-y-4">
                      <div className="bg-violet-50 rounded-lg border border-violet-200 p-4">
                        <h3 className="text-sm font-semibold text-violet-700 mb-3">DataFinder Predictive Scores</h3>

                        {/* Two score boxes side by side */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="bg-white rounded-lg p-3 text-center border">
                            <div className="text-xs text-gray-500 mb-1">Giving Score</div>
                            <div className="text-2xl font-bold text-violet-600">
                              {versiumScores?.donor_score ?? '—'}
                            </div>
                            <div className="text-xs text-gray-400">0–99 scale</div>
                          </div>
                          <div className="bg-white rounded-lg p-3 text-center border">
                            <div className="text-xs text-gray-500 mb-1">Wealth Score</div>
                            <div className="text-2xl font-bold text-violet-500">
                              {versiumScores?.wealth_score ?? '—'}
                            </div>
                            <div className="text-xs text-gray-400">0–99 scale</div>
                          </div>
                        </div>

                        {/* Fetched timestamp */}
                        {versiumScores?.fetched_at && (
                          <p className="text-xs text-gray-500 mb-3">
                            Fetched: {new Date(versiumScores.fetched_at).toLocaleString()}
                          </p>
                        )}

                        {/* Address quality indicator */}
                        <p className="text-xs mb-3">
                          {contactDetail.has_address
                            ? '✓ Address on file (higher accuracy)'
                            : '⚠ No address — results may be less accurate'}
                        </p>

                        {/* Buttons — hidden if no exec_contact_id */}
                        {contactDetail.exec_contact_id ? (
                          <div className="flex gap-2">
                            <button onClick={() => fetchVersiumScore('donor')}
                              disabled={versiumLoading}
                              className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50">
                              {versiumLoading ? 'Fetching...' : 'Get Giving Score'}
                            </button>
                            <button onClick={() => fetchVersiumScore('wealth')}
                              disabled={versiumLoading}
                              className="px-3 py-1.5 text-xs bg-violet-500 text-white rounded hover:bg-violet-600 disabled:opacity-50">
                              {versiumLoading ? 'Fetching...' : 'Get Wealth Score'}
                            </button>
                            <button onClick={() => fetchVersiumScore('both')}
                              disabled={versiumLoading}
                              className="px-3 py-1.5 text-xs bg-violet-700 text-white rounded hover:bg-violet-800 disabled:opacity-50">
                              {versiumLoading ? 'Fetching...' : 'Get Both'}
                            </button>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 italic">
                            No executive contact linked — scores require an exec contact record.
                          </p>
                        )}

                        {/* Error display */}
                        {versiumError && (
                          <p className="mt-2 text-xs text-red-600">{versiumError}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Outreach Tab */}
                  {detailTab === 'outreach' && (
                    <div>
                      {contactDetail.active_outreach ? (
                        <div className="bg-white rounded-lg border p-4">
                          <h3 className="text-sm font-semibold text-violet-700 mb-3">Active Outreach Assignment</h3>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div><span className="text-gray-500">Assigned to:</span> <span className="font-medium">{contactDetail.active_outreach.assigned_to_name}</span></div>
                            <div><span className="text-gray-500">Stage:</span> <span className="font-medium capitalize">{contactDetail.active_outreach.stage}</span></div>
                            <div><span className="text-gray-500">Priority:</span> <span className="capitalize">{contactDetail.active_outreach.priority || 'normal'}</span></div>
                            <div><span className="text-gray-500">Due:</span> {contactDetail.active_outreach.due_date || 'No due date'}</div>
                            {contactDetail.active_outreach.goal && <div className="col-span-2"><span className="text-gray-500">Goal:</span> {contactDetail.active_outreach.goal}</div>}
                            {contactDetail.active_outreach.purpose && <div className="col-span-2"><span className="text-gray-500">Purpose:</span> {contactDetail.active_outreach.purpose}</div>}
                            <div><span className="text-gray-500">Touchpoints:</span> {contactDetail.active_outreach.touchpoint_count}</div>
                            {contactDetail.active_outreach.last_touchpoint_at && <div><span className="text-gray-500">Last touch:</span> {new Date(contactDetail.active_outreach.last_touchpoint_at).toLocaleDateString()}</div>}
                          </div>
                          <div className="mt-3 pt-3 border-t">
                            <button
                              onClick={() => navigate('/admin/alumni-research-assignments?tab=outreach')}
                              className="text-xs text-violet-600 hover:text-violet-800 hover:underline"
                            >
                              View in Assignments page &rarr;
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 py-4">
                          <div className="text-center mb-4">No active outreach assignment.</div>

                          {outreachSuccess && (
                            <div className="mb-3 p-2 bg-green-50 text-green-700 text-xs rounded border border-green-200">{outreachSuccess}</div>
                          )}

                          {isSuperAdmin && !showAssignOutreach && (
                            <div className="flex items-center justify-center gap-3">
                              <button
                                onClick={() => { setShowAssignOutreach(true); setOutreachSuccess(null); loadAdminStaff(); }}
                                className="px-4 py-2 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700"
                              >
                                Assign Outreach
                              </button>
                              <button
                                onClick={() => navigate('/admin/outreach-manager')}
                                className="px-4 py-2 text-xs text-violet-600 border border-violet-300 rounded-lg hover:bg-violet-50"
                              >
                                Outreach Manager
                              </button>
                            </div>
                          )}

                          {!isSuperAdmin && (
                            <div className="text-center">
                              <button
                                onClick={() => navigate('/admin/alumni-research-assignments?tab=outreach')}
                                className="px-4 py-2 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700"
                              >
                                Go to Outreach Assignments
                              </button>
                            </div>
                          )}

                          {showAssignOutreach && (
                            <div className="bg-violet-50 rounded-lg border border-violet-200 p-4 mt-3">
                              <h4 className="text-sm font-semibold text-violet-700 mb-3">Create Outreach Assignment</h4>
                              {outreachError && (
                                <div className="mb-3 p-2 bg-red-50 text-red-700 text-xs rounded border border-red-200">{outreachError}</div>
                              )}
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Assign To *</label>
                                  <select
                                    value={outreachForm.assigned_to_id}
                                    onChange={e => setOutreachForm(f => ({ ...f, assigned_to_id: e.target.value }))}
                                    className="w-full border rounded px-2 py-1.5 text-xs"
                                  >
                                    <option value="">Select staff member...</option>
                                    {adminStaff.map(a => (
                                      <option key={a.id} value={a.id}>{a.full_name || a.email}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Goal</label>
                                    <select
                                      value={outreachForm.goal}
                                      onChange={e => setOutreachForm(f => ({ ...f, goal: e.target.value }))}
                                      className="w-full border rounded px-2 py-1.5 text-xs"
                                    >
                                      <option value="engagement">Engagement</option>
                                      <option value="membership_upgrade">Membership Upgrade</option>
                                      <option value="donation">Donation</option>
                                      <option value="event_attendance">Event Attendance</option>
                                      <option value="board_recruitment">Board Recruitment</option>
                                      <option value="other">Other</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                                    <select
                                      value={outreachForm.priority}
                                      onChange={e => setOutreachForm(f => ({ ...f, priority: e.target.value }))}
                                      className="w-full border rounded px-2 py-1.5 text-xs"
                                    >
                                      <option value="low">Low</option>
                                      <option value="normal">Normal</option>
                                      <option value="high">High</option>
                                      <option value="urgent">Urgent</option>
                                    </select>
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
                                  <input
                                    type="date"
                                    value={outreachForm.due_date}
                                    onChange={e => setOutreachForm(f => ({ ...f, due_date: e.target.value }))}
                                    className="w-full border rounded px-2 py-1.5 text-xs"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Purpose</label>
                                  <textarea
                                    value={outreachForm.purpose}
                                    onChange={e => setOutreachForm(f => ({ ...f, purpose: e.target.value }))}
                                    className="w-full border rounded px-2 py-1.5 text-xs"
                                    rows={2}
                                    maxLength={500}
                                    placeholder="Brief description of outreach goal..."
                                  />
                                </div>
                                <div className="flex items-center justify-end gap-2 pt-1">
                                  <button
                                    onClick={() => { setShowAssignOutreach(false); setOutreachError(null); }}
                                    className="px-3 py-1.5 text-xs text-gray-600 border rounded hover:bg-gray-50"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleAssignOutreach}
                                    disabled={!outreachForm.assigned_to_id || outreachSubmitting}
                                    className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50"
                                  >
                                    {outreachSubmitting ? 'Creating...' : 'Create Assignment'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* LinkedIn Activity Tab */}
                  {detailTab === 'linkedin-activity' && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-700">LinkedIn Comments ({linkedinCommentsTotal})</h3>
                        <button
                          onClick={() => fetchLinkedInActivity(selectedContactId, selectedContactType)}
                          disabled={linkedinCommentsLoading}
                          className="px-3 py-1.5 text-xs bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50"
                        >
                          {linkedinCommentsLoading ? 'Loading...' : 'Refresh'}
                        </button>
                      </div>

                      {linkedinCommentsLoading && linkedinComments.length === 0 ? (
                        <div className="text-center text-sm text-gray-500 py-8">Loading LinkedIn activity...</div>
                      ) : linkedinComments.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-sm text-gray-500 mb-2">No LinkedIn comments imported yet</p>
                          <p className="text-xs text-gray-400">Use the Executive Intelligence page to import comments for this contact.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {linkedinComments.map(c => (
                            <div key={c.id} className="bg-white rounded-lg border p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="text-xs text-gray-500">
                                  {c.post_author_name && <span>Commented on <span className="font-medium text-gray-700">{c.post_author_name}</span>'s post</span>}
                                  {c.commented_at && <span> &middot; {new Date(c.commented_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                                </div>
                                {c.post_url && (
                                  <a href={c.post_url} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-600 hover:underline flex-shrink-0 ml-2">
                                    View on LinkedIn
                                  </a>
                                )}
                              </div>
                              {c.post_text_snippet && (
                                <div className="text-xs text-gray-400 italic mb-2 line-clamp-2 border-l-2 border-gray-200 pl-2">
                                  {c.post_text_snippet}
                                </div>
                              )}
                              <p className="text-sm text-gray-800">{c.comment_text}</p>
                              {(c.num_likes > 0 || c.num_replies > 0) && (
                                <div className="flex gap-3 mt-2 text-xs text-gray-500">
                                  {c.num_likes > 0 && <span>{c.num_likes} like{c.num_likes !== 1 ? 's' : ''}</span>}
                                  {c.num_replies > 0 && <span>{c.num_replies} repl{c.num_replies !== 1 ? 'ies' : 'y'}</span>}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Timeline Tab */}
                  {detailTab === 'timeline' && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-gray-700">Activity Timeline ({timelineTotal} events)</h3>
                      </div>

                      {timelineLoading ? (
                        <div className="text-center text-sm text-gray-500 py-8">Loading timeline...</div>
                      ) : timelineEvents.length === 0 ? (
                        <div className="text-center text-sm text-gray-500 py-8">No activity recorded</div>
                      ) : (
                        <div className="relative">
                          {/* Vertical line */}
                          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                          <div className="space-y-4">
                            {timelineEvents.map((evt, idx) => {
                              const cfg = eventTypeConfig[evt.type] || eventTypeConfig.note;
                              return (
                                <div key={`${evt.type}-${evt.id}-${idx}`} className="relative flex items-start gap-3 pl-2">
                                  {/* Icon circle */}
                                  <div className={`relative z-10 flex-shrink-0 w-7 h-7 rounded-full ${cfg.bg} ${cfg.text} flex items-center justify-center text-xs font-bold border ${cfg.border}`}>
                                    {cfg.icon}
                                  </div>

                                  {/* Event card */}
                                  <div className="flex-1 bg-white rounded-lg border p-3 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-sm font-medium text-gray-900">{evt.title}</span>
                                      <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">
                                        {evt.timestamp ? new Date(evt.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                                      </span>
                                    </div>
                                    {evt.detail && (
                                      <div className="text-xs text-gray-600 mt-0.5">{evt.detail}</div>
                                    )}
                                    {evt.notes_text && (
                                      <div className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{evt.notes_text}</div>
                                    )}
                                    {evt.author && (
                                      <div className="text-[10px] text-gray-400 mt-1">by {evt.author}</div>
                                    )}
                                    {evt.outcome && (
                                      <span className={`inline-block mt-1 px-1.5 py-0.5 text-[10px] rounded font-medium ${
                                        evt.outcome === 'connected' || evt.outcome === 'replied' ? 'bg-green-100 text-green-700' :
                                        evt.outcome === 'no_answer' || evt.outcome === 'voicemail' ? 'bg-yellow-100 text-yellow-700' :
                                        evt.outcome === 'bounced' ? 'bg-red-100 text-red-700' :
                                        'bg-gray-100 text-gray-600'
                                      }`}>
                                        {evt.outcome}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Timeline pagination */}
                          {timelineTotal > 50 && (
                            <div className="flex items-center justify-center gap-3 mt-4 pt-3 border-t">
                              <button
                                onClick={() => fetchTimeline(selectedContactId, timelinePage - 1, selectedContactType)}
                                disabled={timelinePage <= 1}
                                className="px-3 py-1 text-xs border rounded disabled:opacity-50"
                              >
                                Newer
                              </button>
                              <span className="text-xs text-gray-500">Page {timelinePage}</span>
                              <button
                                onClick={() => fetchTimeline(selectedContactId, timelinePage + 1, selectedContactType)}
                                disabled={timelinePage * 50 >= timelineTotal}
                                className="px-3 py-1 text-xs border rounded disabled:opacity-50"
                              >
                                Older
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Relationship Memory Tab */}
                  {detailTab === 'relationship' && contactDetail.exec_person_id && (
                    <div className="space-y-4">
                      {relMemoryLoading ? (
                        <div className="text-center text-sm text-gray-500 py-8">Loading relationship memory...</div>
                      ) : relMemorySummary?.memory ? (
                        <>
                          {/* Do Not Contact Warning */}
                          {relMemorySummary.memory.do_not_contact && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                              <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                              <span className="text-sm font-medium text-red-800">Do Not Contact — This person has been flagged as do-not-contact</span>
                            </div>
                          )}

                          {/* Summary Cards */}
                          <div className="grid grid-cols-3 gap-4">
                            {/* Receptivity Score */}
                            <div className="bg-white rounded-lg border p-4">
                              <h4 className="text-xs font-medium text-gray-500 mb-2">Receptivity Score</h4>
                              {(relMemorySummary.memory.total_requests_received || 0) > 0 ? (
                                <>
                                  <div className="text-2xl font-bold text-gray-900">
                                    {relMemorySummary.memory.receptivity_score != null
                                      ? (relMemorySummary.memory.receptivity_score * 100).toFixed(0) + '%'
                                      : 'N/A'}
                                  </div>
                                  <div className="w-full h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
                                    <div
                                      className={`h-full rounded-full transition-all ${
                                        (relMemorySummary.memory.receptivity_score || 0) >= 0.7 ? 'bg-green-500' :
                                        (relMemorySummary.memory.receptivity_score || 0) >= 0.4 ? 'bg-yellow-500' : 'bg-red-500'
                                      }`}
                                      style={{ width: `${(relMemorySummary.memory.receptivity_score || 0) * 100}%` }}
                                    />
                                  </div>
                                </>
                              ) : (
                                <div className="text-sm text-gray-400">No data yet</div>
                              )}
                            </div>

                            {/* Cooldown Status */}
                            <div className="bg-white rounded-lg border p-4">
                              <h4 className="text-xs font-medium text-gray-500 mb-2">Cooldown Status</h4>
                              {relMemorySummary.cooldown?.is_on_cooldown ? (
                                <div>
                                  <div className="text-sm font-semibold text-amber-600 flex items-center gap-1">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    On Cooldown
                                  </div>
                                  {relMemorySummary.cooldown.cooldown_until && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      Until {new Date(relMemorySummary.cooldown.cooldown_until).toLocaleDateString()}
                                    </div>
                                  )}
                                  {relMemorySummary.cooldown.cooldown_reason && (
                                    <div className="text-xs text-gray-400 mt-0.5">{relMemorySummary.cooldown.cooldown_reason}</div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-sm font-semibold text-green-600 flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  Available
                                </div>
                              )}
                            </div>

                            {/* Engagement Summary */}
                            <div className="bg-white rounded-lg border p-4">
                              <h4 className="text-xs font-medium text-gray-500 mb-2">Engagement History</h4>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Accepted</span>
                                  <span className="font-medium text-green-600">{relMemorySummary.memory.total_accepted || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Declined</span>
                                  <span className="font-medium text-red-600">{relMemorySummary.memory.total_declined || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">No Response</span>
                                  <span className="font-medium text-gray-500">{relMemorySummary.memory.total_no_response || 0}</span>
                                </div>
                                <div className="flex justify-between border-t pt-1 mt-1">
                                  <span className="text-gray-500">Total</span>
                                  <span className="font-medium text-gray-900">{relMemorySummary.memory.total_requests_received || 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Preferences */}
                          {(relMemorySummary.memory.preferred_contact_channel || relMemorySummary.memory.preferred_cadence || relMemorySummary.memory.notes) && (
                            <div className="bg-white rounded-lg border p-4">
                              <h4 className="text-xs font-medium text-gray-500 mb-3">Contact Preferences</h4>
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                {relMemorySummary.memory.preferred_contact_channel && (
                                  <div><span className="text-gray-500">Channel:</span> <span className="text-gray-900 capitalize">{relMemorySummary.memory.preferred_contact_channel}</span></div>
                                )}
                                {relMemorySummary.memory.preferred_cadence && (
                                  <div><span className="text-gray-500">Cadence:</span> <span className="text-gray-900 capitalize">{relMemorySummary.memory.preferred_cadence}</span></div>
                                )}
                                {relMemorySummary.memory.open_to_mentorship != null && (
                                  <div><span className="text-gray-500">Mentorship:</span> <span className={relMemorySummary.memory.open_to_mentorship ? 'text-green-600 font-medium' : 'text-gray-500'}>{relMemorySummary.memory.open_to_mentorship ? 'Open' : 'Not open'}</span></div>
                                )}
                                {relMemorySummary.memory.open_to_board_inquiries != null && (
                                  <div><span className="text-gray-500">Board Inquiry:</span> <span className={relMemorySummary.memory.open_to_board_inquiries ? 'text-green-600 font-medium' : 'text-gray-500'}>{relMemorySummary.memory.open_to_board_inquiries ? 'Open' : 'Not open'}</span></div>
                                )}
                                {relMemorySummary.memory.notes && (
                                  <div className="col-span-2"><span className="text-gray-500">Notes:</span> <span className="text-gray-900">{relMemorySummary.memory.notes}</span></div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Last Contact */}
                          {relMemorySummary.memory.last_contacted_at && (
                            <div className="bg-white rounded-lg border p-4">
                              <div className="text-xs text-gray-500">
                                Last contacted: <span className="text-gray-700 font-medium">{new Date(relMemorySummary.memory.last_contacted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                {relMemorySummary.memory.last_contact_outcome && (
                                  <span className="ml-2 text-gray-400">— {relMemorySummary.memory.last_contact_outcome}</span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Open Full Viewer Button */}
                          <button
                            onClick={() => setShowRelMemory(true)}
                            className="w-full px-4 py-2.5 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                          >
                            Open Full Relationship Memory (edit preferences, view timeline)
                          </button>
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          <p className="text-sm text-gray-500">No relationship memory found for this contact.</p>
                          <p className="text-xs text-gray-400 mt-1">Memory records are created after the first outreach or brokerage attempt.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* Relationship Memory Modal */}
            {showRelMemory && contactDetail?.exec_person_id && (
              <RelationshipMemoryViewer
                execPersonId={contactDetail.exec_person_id}
                execPersonName={contactDetail.full_name || `${contactDetail.first_name || ''} ${contactDetail.last_name || ''}`.trim()}
                onClose={() => setShowRelMemory(false)}
                onUpdate={() => {
                  fetchRelMemorySummary(contactDetail.exec_person_id);
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCRM;
